import { Text } from '@ui/components/Text';
import { cssValueToPixels } from '@ui/tokens/mathUtils';
import { BandScaleConfig, LinearScaleConfig, scaleLinear } from '@visx/scale';
import { getStringWidth } from '@visx/text';
import { AnimatedAxis, AnimatedBarSeries, Axis, BarSeries, BarStack, Margin, XYChart } from '@visx/xychart';
import isChromatic from 'chromatic/isChromatic';
import { useMemo } from 'react';
import { useReducedMotion } from 'react-spring';

import tokens from '../../../tokens/_variables.json';

import { useI18n } from '@ocula/i18n-formatters';
import { BarChartAnnotation } from './BarChartAnnotation';

export type BarChartProps<DatumT extends object> = {
  width: number;
  data: Array<DatumT>;
  xAccessor: (d: DatumT) => number;
  yAccessor: (d: DatumT) => string;
  colorAccessor: (d: DatumT) => string;
  accessibilityLabel: string;
  background?: {
    type: 'bar';
    xMax: number;
  };
  dataLabelAccessor?: (d: DatumT) => { label: string; color: string; colorLightBackground?: string };
  dataSubLabelAccessor?: (d: DatumT) => string | undefined;
  getPrimaryDatum?: (d: DatumT) => boolean;
  hideAxis?: boolean;
  radiiBar?: boolean;
  /** forces a minimum amount of margin to protect against
  aggressive text wrapping */
  minLabelWidth?: number;
  className?: string;
  labelClassName?: string;
  showXAxisLabels?: boolean;
  /** Renders values as a stacked bar along the X axis, enabling value-based colour indicators (e.g. < 60% is red)  */
  xAxisBarStack?: Array<{ id: string; color: string; value: number }>;
};

const defaultMinLabelWidth = 150;
const labelPaddingRight = cssValueToPixels(tokens['spacing-s']);
const labelOffsetX = -labelPaddingRight;
const marginDefault: Margin = { left: defaultMinLabelWidth, right: 16, top: 0, bottom: 0 };
const minBarWidth = 60;
// Exact pixel height of bar and padding
const barTotalHeight = 50;
// Total height of the padding either side of the bar
const barPaddingPx = 16;
// Ratio of padding to bar height
const barPaddingRatio = barPaddingPx / barTotalHeight;
const barHeight = barTotalHeight * (1 - barPaddingRatio);
const paddingHeight = barTotalHeight * barPaddingRatio;
const xScaleConfig: LinearScaleConfig<number> = { type: 'linear', nice: true, round: true } as const;
const yScaleConfig: BandScaleConfig<string> = { type: 'band', padding: barPaddingRatio } as const;

const defaultTickLabelProps = {
  dx: labelOffsetX,
  width: defaultMinLabelWidth - labelPaddingRight - Math.abs(labelOffsetX),
  ...tokens['text-base'],
  fill: tokens.text.color.primary.body,
};

const getAxisLabelDy = (label: string, width: number) => {
  const axisLabelFontSize = cssValueToPixels(tokens['text-base'].fontSize);
  const words = label.split(' ');
  if (words.length === 1) {
    // Offset single line text as verticalAnchor cannot be set to middle for axis labels
    return 0.4 * axisLabelFontSize;
  }
  const lengths = words
    .map((s: string) =>
      getStringWidth(s, {
        ...defaultTickLabelProps,
        width: undefined,
        dx: undefined,
      }),
    )
    .filter((s) => !!s);
  const lines = lengths.reduce<Array<number>>(
    (acc, curr) => {
      const lineWidth = (acc.at(-1) ?? 0) + (curr ?? 0);
      if (lineWidth > width) {
        acc.push(curr ?? 0);
        return acc;
      }
      return [...acc.slice(0, -1), lineWidth];
    },
    [0],
  );
  // Offset multiline text by number of lines
  const axisLabelLineHeight = cssValueToPixels(
    tokens['text-base'].lineHeight,
    cssValueToPixels(tokens['text-base'].fontSize),
  );
  return 0.4 * axisLabelFontSize + (lines.length - 1) * 0.5 * axisLabelLineHeight;
};

const parentWidthSufficient = (width: number, marginFinal: Margin) =>
  width - marginFinal.left - marginFinal.right > minBarWidth;

function getLongestLabel<DatumT extends object>(data: Array<DatumT>, labelAccessor: (d: DatumT) => string) {
  return data
    .flatMap((d) => labelAccessor(d).split(' '))
    .sort((a, b) => a.length - b.length)
    .at(-1);
}

// Value determined by experimentation with different label lengths. Required as the getStringWidth
// helper function does not accurately calculate widths for strings with applied styles.
const labelWidthFudgeFactor = 1.25;

function getMargins<DatumT extends object>(
  data: Array<DatumT>,
  yAccessor: (d: DatumT) => string,
  minLabelWidth?: number,
) {
  const longestLabelText = getLongestLabel(data, yAccessor);
  const longestLabelWidth = getStringWidth(longestLabelText ?? '', {
    ...defaultTickLabelProps,
    width: undefined,
    dx: undefined,
  });
  const labelExists = !!longestLabelWidth;
  const minLeftMargin = (longestLabelWidth ?? 0) * labelWidthFudgeFactor + (labelExists ? 2 * labelPaddingRight : 0);
  return { ...marginDefault, left: Math.max(minLeftMargin, minLabelWidth ?? marginDefault.left) };
}

const tickLabelProps = (label: string, textWidth: number) => ({
  ...defaultTickLabelProps,
  width: textWidth,
  dy: getAxisLabelDy(label, textWidth),
  lineHeight: cssValueToPixels(tokens['text-meta'].lineHeight, cssValueToPixels(tokens['text-meta'].fontSize)),
});

const EmptyTick = () => undefined;

export function BarChart<DatumT extends object>({
  width,
  data,
  xAccessor,
  yAccessor,
  colorAccessor,
  accessibilityLabel,
  background,
  dataLabelAccessor,
  dataSubLabelAccessor,
  getPrimaryDatum,
  hideAxis,
  radiiBar,
  minLabelWidth,
  className,
  labelClassName,
  showXAxisLabels,
  xAxisBarStack,
}: BarChartProps<DatumT>) {
  const itemCount = data.length;
  const margin = useMemo(() => getMargins(data, yAccessor, minLabelWidth), [data, yAccessor, minLabelWidth]);
  const display = useMemo(() => parentWidthSufficient(width, margin), [width, margin]);
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = itemCount * barHeight + (itemCount + 1) * paddingHeight;
  const height = chartHeight + margin.top + margin.bottom;

  const axisLeft = useMemo(
    () => margin.left - cssValueToPixels(tokens['spacing-xs']) - cssValueToPixels(tokens['border-highlight']),
    [margin.left],
  );
  const axisHighlightPosition = useMemo(() => {
    if (!getPrimaryDatum) {
      return undefined;
    }
    const primaryIndex = data.findIndex((d) => getPrimaryDatum(d));
    if (primaryIndex === -1) {
      return undefined;
    }
    return {
      start: (chartHeight / itemCount) * primaryIndex,
      end: (chartHeight / itemCount) * (itemCount - primaryIndex - 1),
    };
  }, [data, itemCount, getPrimaryDatum, chartHeight]);
  const textWidth = axisLeft - labelPaddingRight - Math.abs(labelOffsetX);

  const xScale = useMemo(
    () =>
      scaleLinear({
        ...xScaleConfig,
        domain: [0, Math.max(...data.map((datum) => xAccessor(datum)), background?.xMax ?? 0)],
        range: [0, chartWidth],
      }),
    [data, background?.xMax, chartWidth, xAccessor],
  );
  // TODO move reduced motion to shared app state
  const reducedMotion = useReducedMotion() ?? isChromatic();
  const RenderedBarSeries = reducedMotion ? BarSeries : AnimatedBarSeries;
  const RenderedAxis = reducedMotion ? Axis : AnimatedAxis;
  const { formatter } = useI18n();
  return display ? (
    <div className={className}>
      <XYChart
        width={width}
        height={height}
        margin={margin}
        xScale={xScaleConfig}
        yScale={yScaleConfig}
        horizontal
        accessibilityLabel={accessibilityLabel}
      >
        {background?.type === 'bar' && (
          <RenderedBarSeries
            dataKey="background"
            // @ts-expect-error
            className={className}
            data={data}
            xAccessor={() => background.xMax}
            yAccessor={yAccessor}
            // TODO update to all `color-shade-x` values to alias tokens once they are available.
            colorAccessor={() => (className ? 'currentColor' : tokens['color-shade-lightest'])}
            radius={radiiBar ? 4 : 0}
            radiusAll={radiiBar}
          />
        )}
        <RenderedBarSeries
          dataKey="bars"
          data={data}
          xAccessor={xAccessor}
          yAccessor={yAccessor}
          colorAccessor={colorAccessor}
          radius={radiiBar ? 4 : 0}
          radiusAll={radiiBar}
        />
        <RenderedAxis
          animationTrajectory="max"
          orientation="left"
          hideTicks
          hideAxisLine={hideAxis}
          tickLabelProps={(d) => tickLabelProps(d, textWidth)}
          stroke={tokens['color-shade-light']}
          strokeWidth={tokens['border-highlight']}
          left={axisLeft}
        />
        {axisHighlightPosition && (
          <RenderedAxis
            animationTrajectory="max"
            orientation="left"
            hideTicks
            left={axisLeft}
            rangePadding={axisHighlightPosition}
            tickComponent={EmptyTick}
            stroke={tokens['color-shade-dark']}
            strokeWidth={tokens['border-highlight']}
          />
        )}
        {dataLabelAccessor &&
          data.map((d) => {
            const { label, color, colorLightBackground } = dataLabelAccessor(d);
            const barX = xScale(xAccessor(d));
            return (
              <BarChartAnnotation
                barX={barX}
                chartWidth={chartWidth}
                chartMarginLeft={margin.left}
                datum={d}
                title={label}
                color={color}
                colorLightBackground={colorLightBackground}
                key={`${yAccessor(d)}-${xAccessor(d)}`}
                subTitle={dataSubLabelAccessor?.(d)}
                labelClassName={labelClassName}
                dataKey="bars"
              />
            );
          })}
      </XYChart>
      {(showXAxisLabels || xAxisBarStack) && (
        <XYChart
          width={width}
          height={30}
          margin={{ ...margin, bottom: 24, top: 0 }}
          xScale={xScaleConfig}
          yScale={{ ...yScaleConfig, paddingOuter: 0 }}
          horizontal
        >
          {showXAxisLabels && (
            <RenderedAxis
              key="xAxis"
              orientation="bottom"
              animationTrajectory="min"
              hideAxisLine
              hideTicks
              tickFormat={(v) => formatter.formatPercentage(v)}
              tickLabelProps={{ ...tokens['text-meta'], fill: tokens.text.color.primary.body, dy: 16 }}
              numTicks={chartWidth > 400 ? 10 : 5}
            />
          )}
          {xAxisBarStack && (
            <BarStack>
              {xAxisBarStack.map(({ id, color, value }, i) => (
                <BarSeries
                  key={id}
                  dataKey={id}
                  data={[
                    {
                      id,
                      color,
                      value,
                    },
                  ]}
                  xAccessor={(d) => d.value}
                  yAccessor={(_) => ''}
                  colorAccessor={(d) => d.color}
                  radius={999}
                  // Only left radii the first bar to prevent white gaps
                  radiusLeft={i === 0}
                  // Only right radii the last bar to prevent white gaps
                  // Radii only the right-most bar in the stack.
                  radiusRight={i === xAxisBarStack.length - 1}
                />
              ))}
            </BarStack>
          )}
        </XYChart>
      )}
    </div>
  ) : (
    <Text variant="meta">Could not display chart</Text>
  );
}
